import { useEffect, useRef } from "react"

interface SofiaEventMap {
  TREATMENT_PLAN_SUGGESTION_APPEND_EVENT: CustomEvent<{ progressNoteId: string }>
  AI_FEATURE_RUNNING_EVENT: CustomEvent<boolean>
}

type EventListener<K extends keyof SofiaEventMap> = (event: SofiaEventMap[K]) => void

const useSofiaEventListener = <K extends keyof SofiaEventMap>(eventName: K, handler: EventListener<K>): void => {
  const handlerRef = useRef<EventListener<K>>(handler)

  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    const listener = ((event: SofiaEventMap[K]) => handlerRef.current(event)) as EventListenerOrEventListenerObject
    document.addEventListener(eventName, listener)

    return () => {
      document.removeEventListener(eventName, listener)
    }
  }, [eventName])
}

const sofiaEventDispatch = <K extends keyof SofiaEventMap>(event: SofiaEventMap[K]) => {
  document.dispatchEvent(event)
}

const CustomSofiaEvent = <K extends keyof SofiaEventMap>(
  eventName: K,
  detail: SofiaEventMap[K]["detail"],
): CustomEvent<SofiaEventMap[K]["detail"]> => {
  return new CustomEvent(eventName, {
    detail,
  })
}

export { useSofiaEventListener, sofiaEventDispatch, CustomSofiaEvent }
